import { useState } from 'react';
import { motion } from 'motion/react';
import { FireExtinguisher, Plus, Pencil, Trash2 } from 'lucide-react';
import { useApp } from '@/store';
import { PressureClass, type DeviceType } from '@/data/types';
import { useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Form = {
  id: string;
  nameDe: string;
  nameEn: string;
  pressureClass: PressureClass;
  ageLimit: string;
};
const blank = (): Form => ({
  id: '',
  nameDe: '',
  nameEn: '',
  pressureClass: PressureClass.BOOST,
  ageLimit: '20',
});
const fromType = (dt: DeviceType): Form => ({
  id: dt.id,
  nameDe: dt.nameDe,
  nameEn: dt.nameEn,
  pressureClass: dt.pressureClass,
  ageLimit: String(dt.ageLimit),
});

export function DeviceTypes() {
  const t = useT();
  const types = useApp(s => s.deviceTypes);
  const upsertDeviceType = useApp(s => s.upsertDeviceType);
  const deleteDeviceType = useApp(s => s.deleteDeviceType);
  const toast = useApp(s => s.toast);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DeviceType | null>(null);
  const [form, setForm] = useState<Form>(blank());

  const openCreate = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };
  const openEdit = (dt: DeviceType) => {
    setEditing(dt);
    setForm(fromType(dt));
    setOpen(true);
  };
  const handleSave = () => {
    if (!form.id.trim() || !form.nameDe.trim()) return;
    upsertDeviceType({
      id: form.id.trim().toLowerCase().replace(/\s+/g, '-'),
      nameDe: form.nameDe.trim(),
      nameEn: form.nameEn.trim(),
      pressureClass: form.pressureClass,
      ageLimit: parseInt(form.ageLimit, 10) || 20,
    });
    toast(editing ? t('common.save') : t('types.newType'));
    setOpen(false);
  };
  const handleDelete = (dt: DeviceType) => {
    if (!confirm(`${t('common.delete')} ${dt.nameDe}?`)) return;
    deleteDeviceType(dt.id);
    toast(t('common.delete'));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FireExtinguisher className="h-6 w-6 text-primary" />{' '}
            {t('types.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('types.subtitle')}</p>
        </div>
        <Button size="sm" onClick={openCreate} data-focus="types-new">
          <Plus className="h-4 w-4" />
          {t('types.newType')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            {t('mfr.catalog')} ({types.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">{t('table.code')}</TableHead>
                <TableHead>{t('table.german')}</TableHead>
                <TableHead>{t('table.english')}</TableHead>
                <TableHead>{t('ext.pressure')}</TableHead>
                <TableHead className="text-right">
                  {t('ext.ageLimit')}
                </TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {types.map(dt => (
                <TableRow key={dt.id}>
                  <TableCell className="font-mono text-xs">{dt.id}</TableCell>
                  <TableCell className="font-medium">{dt.nameDe}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {dt.nameEn}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        dt.pressureClass === 'boost' ? 'warning' : 'info'
                      }
                      className="capitalize"
                    >
                      {dt.pressureClass}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {dt.ageLimit}
                    {t('types.years')}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 sm:h-7 sm:w-7"
                        onClick={() => openEdit(dt)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 sm:h-7 sm:w-7"
                        onClick={() => handleDelete(dt)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? t('common.edit') : t('types.newType')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>{t('table.code')}</Label>
              <Input
                value={form.id}
                onChange={e => setForm(f => ({ ...f, id: e.target.value }))}
                disabled={!!editing}
                className="font-mono"
              />
            </div>
            <div>
              <Label>{t('table.german')}</Label>
              <Input
                value={form.nameDe}
                onChange={e => setForm(f => ({ ...f, nameDe: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('table.english')}</Label>
              <Input
                value={form.nameEn}
                onChange={e => setForm(f => ({ ...f, nameEn: e.target.value }))}
              />
            </div>
            <div>
              <Label>{t('ext.pressure')}</Label>
              <Select
                value={form.pressureClass}
                onValueChange={v =>
                  setForm(f => ({ ...f, pressureClass: v as PressureClass }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value={PressureClass.BOOST}
                    className="capitalize"
                  >
                    {PressureClass.BOOST}
                  </SelectItem>
                  <SelectItem
                    value={PressureClass.PERMANENT}
                    className="capitalize"
                  >
                    {PressureClass.PERMANENT}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('ext.ageLimit')}</Label>
              <Input
                type="number"
                value={form.ageLimit}
                onChange={e =>
                  setForm(f => ({ ...f, ageLimit: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
